import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, X, GitBranch, Users, UserCircle2, Skull, Flag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REL_TYPES = [
  { value: "parent", label: "Parent / Child", color: "#3b82f6" },
  { value: "siblings", label: "Siblings", color: "#22c55e" },
  { value: "married", label: "Married / Partner", color: "#ec4899" },
  { value: "allies", label: "Allies", color: "#c4a55a" },
  { value: "rivals", label: "Rivals", color: "#f59e0b" },
  { value: "enemies", label: "Enemies", color: "#ef4444" },
  { value: "mentor", label: "Mentor / Student", color: "#8b5cf6" },
  { value: "noble", label: "Noble Bloodline", color: "#a855f7" },
  { value: "secret", label: "Secret Connection", color: "#6b7280" },
  { value: "custom", label: "Custom", color: "#94a3b8" },
];

const ENTITY_TYPES = [
  { value: "character", label: "Player Character", icon: UserCircle2 },
  { value: "npc", label: "NPC", icon: Users },
  { value: "creature", label: "Creature", icon: Skull },
  { value: "faction", label: "Faction", icon: Flag },
];

const getRelColor = (type) => REL_TYPES.find(r => r.value === type)?.color || "#c4a55a";

const RelationshipsPage = () => {
  const { campaignId } = useParams();
  const [trees, setTrees] = useState([]);
  const [activeTree, setActiveTree] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [entities, setEntities] = useState({ characters: [], npcs: [], creatures: [], factions: [] });
  const [loading, setLoading] = useState(true);
  const [dragNode, setDragNode] = useState(null);
  const [addNodeOpen, setAddNodeOpen] = useState(false);
  const [addRelOpen, setAddRelOpen] = useState(false);
  const [relForm, setRelForm] = useState({ source_id: "", target_id: "", relationship_type: "allies", custom_label: "" });
  const [renamingTree, setRenamingTree] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const canvasRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [campaignId]);

  const fetchData = async () => {
    try {
      const [treesRes, npcRes, charRes, creatRes, factRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}/trees`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/npcs`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/characters`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/creatures`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/factions`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setTrees(treesRes.data);
      setEntities({ characters: charRes.data, npcs: npcRes.data, creatures: creatRes.data, factions: factRes.data });
      if (treesRes.data.length > 0) loadTree(treesRes.data[0]);
    } catch {}
    setLoading(false);
  };

  const loadTree = async (tree) => {
    setActiveTree(tree);
    try {
      const [nodesRes, relsRes] = await Promise.all([
        axios.get(`${API}/trees/${tree.id}/nodes`, { withCredentials: true }),
        axios.get(`${API}/trees/${tree.id}/relationships`, { withCredentials: true }),
      ]);
      setNodes(nodesRes.data);
      setRelationships(relsRes.data);
    } catch {}
  };

  const createTree = async () => {
    try {
      const res = await axios.post(`${API}/campaigns/${campaignId}/trees`, { name: "New Family Tree" }, { withCredentials: true });
      setTrees(p => [...p, res.data]);
      loadTree(res.data);
      toast.success("Tree created");
    } catch { toast.error("Failed"); }
  };

  const deleteTree = async (id) => {
    if (!window.confirm("Delete this tree?")) return;
    try {
      await axios.delete(`${API}/trees/${id}`, { withCredentials: true });
      setTrees(p => p.filter(t => t.id !== id));
      if (activeTree?.id === id) { setActiveTree(null); setNodes([]); setRelationships([]); }
      toast.success("Tree deleted");
    } catch { toast.error("Failed"); }
  };

  const addNode = async (entityId, entityType, entityName) => {
    if (!activeTree) return;
    const x = 100 + nodes.length * 180;
    const y = 100 + (nodes.length % 3) * 120;
    try {
      const res = await axios.post(`${API}/trees/${activeTree.id}/nodes`, { entity_id: entityId, entity_type: entityType, entity_name: entityName, x, y }, { withCredentials: true });
      setNodes(p => [...p, res.data]);
      setAddNodeOpen(false);
      toast.success(`Added ${entityName}`);
    } catch { toast.error("Failed"); }
  };

  const removeNode = async (nodeId) => {
    try {
      await axios.delete(`${API}/nodes/${nodeId}`, { withCredentials: true });
      const node = nodes.find(n => n.id === nodeId);
      setNodes(p => p.filter(n => n.id !== nodeId));
      if (node) setRelationships(p => p.filter(r => r.source_id !== node.entity_id && r.target_id !== node.entity_id));
    } catch {}
  };

  const moveNode = async (nodeId, x, y) => {
    try {
      await axios.put(`${API}/nodes/${nodeId}`, { x, y }, { withCredentials: true });
    } catch {}
  };

  const addRelationship = async () => {
    if (!activeTree || !relForm.source_id || !relForm.target_id) return;
    const srcNode = nodes.find(n => n.entity_id === relForm.source_id);
    const tgtNode = nodes.find(n => n.entity_id === relForm.target_id);
    try {
      const res = await axios.post(`${API}/trees/${activeTree.id}/relationships`, {
        source_id: relForm.source_id, source_type: srcNode?.entity_type || "npc", source_name: srcNode?.entity_name || "",
        target_id: relForm.target_id, target_type: tgtNode?.entity_type || "npc", target_name: tgtNode?.entity_name || "",
        relationship_type: relForm.relationship_type, custom_label: relForm.custom_label,
        color: getRelColor(relForm.relationship_type),
      }, { withCredentials: true });
      setRelationships(p => [...p, res.data]);
      setAddRelOpen(false);
      setRelForm({ source_id: "", target_id: "", relationship_type: "allies", custom_label: "" });
      toast.success("Relationship added");
    } catch { toast.error("Failed"); }
  };

  const removeRelationship = async (relId) => {
    try {
      await axios.delete(`${API}/relationships/${relId}`, { withCredentials: true });
      setRelationships(p => p.filter(r => r.id !== relId));
    } catch {}
  };

  const renameTree = async (treeId, name) => {
    try {
      await axios.put(`${API}/trees/${treeId}`, { name }, { withCredentials: true });
      setTrees(p => p.map(t => t.id === treeId ? { ...t, name } : t));
      if (activeTree?.id === treeId) setActiveTree(p => ({ ...p, name }));
      toast.success("Tree renamed");
    } catch { toast.error("Failed to rename"); }
  };

  const handleNodePointerDown = (e, nodeId) => {
    e.stopPropagation();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    setDragNode({ id: nodeId, offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top });
  };

  const handlePointerMove = useCallback((e) => {
    if (!dragNode || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setNodes(p => p.map(n => n.id === dragNode.id ? { ...n, x, y } : n));
  }, [dragNode]);

  const handlePointerUp = useCallback(() => {
    if (dragNode) {
      const node = nodes.find(n => n.id === dragNode.id);
      if (node) moveNode(dragNode.id, node.x, node.y);
      setDragNode(null);
    }
  }, [dragNode, nodes]);

  const getTypeIcon = (type) => {
    const t = ENTITY_TYPES.find(e => e.value === type);
    return t ? t.icon : Users;
  };

  const allEntities = [
    ...entities.characters.map(e => ({ ...e, _type: "character" })),
    ...entities.npcs.map(e => ({ ...e, _type: "npc" })),
    ...entities.creatures.map(e => ({ ...e, _type: "creature" })),
    ...entities.factions.map(e => ({ ...e, _type: "faction" })),
  ];

  if (loading) return <Layout campaignName="Loading..."><div className="flex justify-center py-20"><div className="spinner" /></div></Layout>;

  return (
    <Layout campaignName="Relationships">
      <div className="max-w-full mx-auto h-[calc(100vh-64px)] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
          <div>
            <h1 className="text-2xl font-serif text-foreground">Family Trees & Relationships</h1>
            <p className="text-sm text-muted-foreground">Map connections between characters, NPCs, factions</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-sm" onClick={createTree} data-testid="create-tree-btn"><Plus className="w-3 h-3 mr-1" /> New Tree</Button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Tree list sidebar */}
          <div className="w-48 bg-card border-r border-border p-2 space-y-1 overflow-y-auto flex-shrink-0">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Trees</p>
            {trees.map(t => (
              <div key={t.id} className={`flex items-center gap-1.5 py-1.5 px-2 rounded-sm cursor-pointer group ${activeTree?.id === t.id ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted/30"}`} onClick={() => loadTree(t)} onDoubleClick={() => { setRenamingTree(t.id); setRenameValue(t.name); }} data-testid={`tree-${t.id}`}>
                <GitBranch className="w-3.5 h-3.5 flex-shrink-0" />
                {renamingTree === t.id ? (
                  <input autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)} className="text-xs bg-transparent border-b border-primary/30 outline-none flex-1 text-foreground" onClick={e => e.stopPropagation()} onBlur={() => { if (renameValue.trim()) renameTree(t.id, renameValue); setRenamingTree(null); }} onKeyDown={e => { if (e.key === "Enter") { if (renameValue.trim()) renameTree(t.id, renameValue); setRenamingTree(null); } if (e.key === "Escape") setRenamingTree(null); }} data-testid={`rename-tree-${t.id}`} />
                ) : (
                  <span className="text-xs truncate flex-1">{t.name}</span>
                )}
                <button className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-destructive" onClick={e => { e.stopPropagation(); deleteTree(t.id); }}><Trash2 className="w-2.5 h-2.5" /></button>
              </div>
            ))}
            {trees.length === 0 && <p className="text-xs text-muted-foreground italic text-center py-4">No trees yet</p>}
          </div>

          {/* Canvas */}
          {activeTree ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-muted/10 flex-shrink-0">
                <h3 className="text-sm font-medium text-foreground">{activeTree.name}</h3>
                <Button size="sm" variant="outline" className="h-6 text-[10px] rounded-sm" onClick={() => setAddNodeOpen(true)} data-testid="add-node-btn"><Plus className="w-3 h-3 mr-0.5" /> Add Entity</Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px] rounded-sm" onClick={() => setAddRelOpen(true)} data-testid="add-rel-btn"><GitBranch className="w-3 h-3 mr-0.5" /> Add Link</Button>
                <div className="flex-1" />
                <span className="text-[10px] text-muted-foreground">{nodes.length} nodes, {relationships.length} links</span>
              </div>
              <div ref={canvasRef} className="flex-1 relative bg-[#0f0f1a] overflow-auto" onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp}>
                {/* Relationship lines */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ minWidth: 2000, minHeight: 1200 }}>
                  {relationships.map(rel => {
                    const srcNode = nodes.find(n => n.entity_id === rel.source_id);
                    const tgtNode = nodes.find(n => n.entity_id === rel.target_id);
                    if (!srcNode || !tgtNode) return null;
                    const label = rel.relationship_type === "custom" ? rel.custom_label : REL_TYPES.find(r => r.value === rel.relationship_type)?.label || rel.relationship_type;
                    return (
                      <g key={rel.id}>
                        <line x1={srcNode.x + 60} y1={srcNode.y + 30} x2={tgtNode.x + 60} y2={tgtNode.y + 30} stroke={rel.color || getRelColor(rel.relationship_type)} strokeWidth="2" strokeDasharray={rel.relationship_type === "secret" ? "6 4" : undefined} />
                        <text x={(srcNode.x + tgtNode.x) / 2 + 60} y={(srcNode.y + tgtNode.y) / 2 + 26} fill={rel.color || "#c4a55a"} fontSize="10" textAnchor="middle" className="pointer-events-auto cursor-pointer" onClick={() => removeRelationship(rel.id)}>{label}</text>
                      </g>
                    );
                  })}
                </svg>
                {/* Nodes */}
                {nodes.map(node => {
                  const Icon = getTypeIcon(node.entity_type);
                  return (
                    <div key={node.id} className="absolute cursor-move group" style={{ left: node.x, top: node.y, width: 120 }}
                      onPointerDown={e => handleNodePointerDown(e, node.id)}
                      data-testid={`node-${node.id}`}
                    >
                      <div className="bg-card border border-border rounded-lg p-2 shadow-lg hover:border-primary/40 transition-colors">
                        <div className="flex items-center gap-1.5">
                          <Icon className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="text-xs font-medium text-foreground truncate">{node.entity_name}</span>
                        </div>
                        <p className="text-[9px] text-muted-foreground capitalize mt-0.5">{node.entity_type}</p>
                      </div>
                      <button className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-destructive rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => { e.stopPropagation(); removeNode(node.id); }}><X className="w-2.5 h-2.5 text-white" /></button>
                    </div>
                  );
                })}
                {nodes.length === 0 && <div className="absolute inset-0 flex items-center justify-center"><p className="text-muted-foreground text-sm">Add entities to build your tree</p></div>}
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <GitBranch className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-xl font-serif text-foreground mb-2">No tree selected</h3>
                <p className="text-muted-foreground mb-4">Create a tree to start mapping relationships</p>
                <Button onClick={createTree}><Plus className="w-4 h-4 mr-2" /> Create Tree</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Node Dialog */}
      <Dialog open={addNodeOpen} onOpenChange={setAddNodeOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-serif">Add Entity to Tree</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto mt-2">
            {allEntities.filter(e => !nodes.find(n => n.entity_id === e.id)).map(e => (
              <button key={e.id} className="w-full flex items-center gap-2 p-2 rounded-sm hover:bg-muted/30 text-left" onClick={() => addNode(e.id, e._type, e.name)} data-testid={`add-entity-${e.id}`}>
                {(() => { const I = getTypeIcon(e._type); return <I className="w-4 h-4 text-primary" />; })()}
                <div><p className="text-sm text-foreground">{e.name}</p><p className="text-[10px] text-muted-foreground capitalize">{e._type}</p></div>
              </button>
            ))}
            {allEntities.filter(e => !nodes.find(n => n.entity_id === e.id)).length === 0 && <p className="text-sm text-muted-foreground text-center py-4">All entities already added</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Relationship Dialog */}
      <Dialog open={addRelOpen} onOpenChange={setAddRelOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-serif">Add Relationship</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-sm font-medium block mb-1">From</label>
              <Select value={relForm.source_id || "__none__"} onValueChange={v => setRelForm(p => ({ ...p, source_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="bg-input border-border rounded-sm"><SelectValue placeholder="Select entity" /></SelectTrigger>
                <SelectContent>{nodes.map(n => <SelectItem key={n.entity_id} value={n.entity_id}>{n.entity_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">To</label>
              <Select value={relForm.target_id || "__none__"} onValueChange={v => setRelForm(p => ({ ...p, target_id: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="bg-input border-border rounded-sm"><SelectValue placeholder="Select entity" /></SelectTrigger>
                <SelectContent>{nodes.filter(n => n.entity_id !== relForm.source_id).map(n => <SelectItem key={n.entity_id} value={n.entity_id}>{n.entity_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Relationship</label>
              <Select value={relForm.relationship_type} onValueChange={v => setRelForm(p => ({ ...p, relationship_type: v }))}>
                <SelectTrigger className="bg-input border-border rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{REL_TYPES.map(r => <SelectItem key={r.value} value={r.value}><span style={{ color: r.color }}>{r.label}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
            {relForm.relationship_type === "custom" && (
              <Input value={relForm.custom_label} onChange={e => setRelForm(p => ({ ...p, custom_label: e.target.value }))} placeholder="Custom label..." className="bg-input border-border rounded-sm" />
            )}
            <Button onClick={addRelationship} className="w-full bg-primary text-primary-foreground rounded-sm" data-testid="submit-rel-btn">Add Relationship</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default RelationshipsPage;
